/**
 * Mapbox Configuration
 *
 * Centralized location for Mapbox access tokens and configuration.
 *
 * Supported environment variables (checked in order):
 *   1. MAPBOX_ACCESS_TOKEN (Node.js server-side)
 *   2. NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN (Next.js client-side — bundled to browser)
 *   3. VITE_MAPBOX_ACCESS_TOKEN (Vite — bundled to browser)
 *
 * Set one of these in your environment to enable maps.
 */

/**
 * Get the Mapbox access token from environment variables.
 *
 * Returns undefined if not found (callers handle gracefully — typically by
 * rendering a placeholder).
 *
 * --- Why this code is shaped the way it is ---
 *
 * Downstream bundlers (especially Vite) statically replace
 * `import.meta.env.VITE_*` and `process.env.NEXT_PUBLIC_*` **only when they
 * see the literal dotted access in the bundled output**. tsup minifies this
 * library; if we wrote `const meta = import.meta; meta.env.VITE_TOKEN` or
 * the same expression twice (which would let the minifier extract a shared
 * local), the literal pattern disappears, the consumer's static-replacement
 * pass no longer matches, and the token reaches runtime as `undefined`.
 *
 * Therefore each env var is read via a single, direct, literal property
 * access (e.g. `process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN`), referenced
 * **exactly once** in the function so the minifier has no reason to extract
 * it into a local.
 *
 * Each branch is wrapped in its own try/catch so:
 *   - Referencing `process` in browsers where it's not declared doesn't throw.
 *   - Referencing `import.meta` in environments that disallow it (e.g. CJS
 *     contexts without an `import.meta` proxy) doesn't throw.
 *   - One unavailable global never short-circuits the others.
 */
export function getMapboxTokenFromEnv(): string | undefined {
  // 1. Node.js server-side (real `process.env`, not statically replaced).
  //    Safe to reference `process.env.MAPBOX_ACCESS_TOKEN` once.
  try {
    if (typeof process !== "undefined") {
      const serverToken = process.env.MAPBOX_ACCESS_TOKEN;
      if (serverToken) return serverToken;
    }
  } catch {
    // process not available — fall through
  }

  // 2. Next.js client-side. Webpack/DefinePlugin (and Turbopack) replace the
  //    literal `process.env.NEXT_PUBLIC_*` access at build time. We MUST read
  //    that literal exactly once — see the function-level comment above.
  try {
    if (typeof process !== "undefined") {
      const nextToken = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN;
      if (nextToken) return nextToken;
    }
  } catch {
    // process not available — fall through
  }

  // 3. Vite. Vite replaces the literal `import.meta.env.VITE_*`
  //    access at build time. We MUST keep this as a single dotted literal,
  //    referenced exactly once. Do not alias `import.meta`, do not
  //    destructure `env`, do not pre-read `import.meta.env` into a local.
  try {
    // @ts-expect-error `import.meta` typing depends on tsconfig module target;
    //                  Vite environments always provide `import.meta.env`.
    const viteToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN as string | undefined;
    if (viteToken) return viteToken;
  } catch {
    // import.meta not available — fall through
  }

  return undefined;
}
